import CaptionButtonContainer from "containers/controls/CaptionButtonContainer";
import ControlButton from "components/ControlButton";
import ChapterButtonContainer from "containers/ChapterButtonContainer";
import CuepointDotsContainer from "containers/cuepoint/CuepointDotsContainer";
import FullscreenButton from "components/FullscreenButton";
import PlayButton from "components/PlayButton";
import PropTypes from "prop-types";
import React from "react";
import SliderContainer from "containers/SliderContainer";
import SliderResizableComponent from "components/SliderResizableComponent";
import VolumeControlContainer from "containers/VolumeControlContainer";
import classnames from "classnames";
import { SettingsButton } from "../SettingsButton";
import styles from "./ChapterControls.css";
import { SeekBackButton } from "../SeekBackButton";
import { SeekForwardButton } from "../SeekForwardButton";
import { PlaybackProgressText } from "../PlaybackProgressText";
import { ChapterNavigationButton } from "../ChapterNavigationButton";

function chapterIndexPropType(props, propName, componentName) {
  const propValue = props[propName];

  if (props.includeChapters) {
    if (typeof propValue === "undefined") {
      return new Error(
        `Required prop \`${propName}\` was not specified in \`${componentName}\` It is required when \`props.includeChapters\` is truthy.`,
      );
    }
    if (typeof props[propName] !== "number") {
      return new Error(
        `Prop ${propName} in ${componentName} needs to be a number.`,
      );
    }
  }

  return null;
}

export const ChapterControls = (props) => {
  return (
    <div aria-label="Chapter Controls" className={styles.root}>
      <PlayButton
        handlePlayButtonClick={props.onTogglePlay}
        key="play-button"
        playing={!!props.playing && !props.isChapterCoverShowing}
      />
      {props.showSeekButtons && (
        <>
          <SeekBackButton chapterId={props.chapterId} />
          <SeekForwardButton chapterId={props.chapterId} />
        </>
      )}
      {props.showCoutdownTimerInControls && (
        <PlaybackProgressText time={props.time} duration={props.duration} />
      )}
      <SliderResizableComponent
        className={classnames(
          styles.slider,
          props.border && styles.sliderBorder,
        )}
        key="progress-slider"
      >
        <>
          <SliderContainer
            {...props}
            adPlaying={false}
            chapterId={props.chapterId}
            disabled={props.disableProgressBar}
            hideProgress={props.hideProgressBarProgress}
            onSlideProgress={props.onSlideProgress}
            onSlideStart={props.onSlideStart}
            onSlideStop={props.onSlideStop}
          />
          {props.showCuepoints && (
            <CuepointDotsContainer chapterId={props.chapterId} />
          )}
        </>
      </SliderResizableComponent>
      {props.showChapterNavigationMarkers && (
        <>
          <ControlButton
            border
            onClick={props.onChapterPreviousClick}
            aria-label="previous chapter"
            className={styles.arrow}
          >
            <ChapterNavigationButton
              className={classnames(styles.icon, "font-size--icon--medium")}
              direction="back"
              financeUnlockedStyle={props.financedUnlockedNavigationArrows}
            />
          </ControlButton>
          {props.showChapterNumber && (
            <ChapterButtonContainer
              chapterId={props.chapterId}
              disabled={false}
              key={`${props.chapterId}`}
              on
            />
          )}
          <ControlButton
            border
            onClick={props.onChapterNextClick}
            aria-label="next chapter"
            className={styles.arrow}
          >
            <ChapterNavigationButton
              className={classnames(styles.icon, "font-size--icon--medium")}
              direction="forward"
              financeUnlockedStyle={props.financedUnlockedNavigationArrows}
            />
          </ControlButton>
        </>
      )}
      {props.showSettingsMenu !== "hide" ? (
        <SettingsButton border visible={props.showSettingsMenu === "force"} />
      ) : (
        <CaptionButtonContainer
          chapterId={props.chapterId}
          isChapterCoverShowing={props.isChapterCoverShowing}
        />
      )}
      {props.canControlVolume && <VolumeControlContainer border />}
      <FullscreenButton />
    </div>
  );
};

ChapterControls.propTypes = {
  border: PropTypes.bool,
  canControlVolume: PropTypes.bool.isRequired,
  chapterButtonSize: PropTypes.number.isRequired,
  chapterEntities: PropTypes.array.isRequired,
  chapterId: PropTypes.number,
  chapterIndex: chapterIndexPropType,
  disableProgressBar: PropTypes.bool.isRequired,
  duration: PropTypes.number.isRequired,
  financedUnlockedNavigationArrows: PropTypes.bool.isRequired,
  hideProgressBarProgress: PropTypes.bool.isRequired,
  isChapterCoverShowing: PropTypes.bool.isRequired,
  onChapterNextClick: PropTypes.func.isRequired,
  onChapterPreviousClick: PropTypes.func.isRequired,
  onReturnToParentChapterClick: PropTypes.func,
  onSlideProgress: PropTypes.func.isRequired,
  onSlideStart: PropTypes.func.isRequired,
  onSlideStop: PropTypes.func.isRequired,
  onTogglePlay: PropTypes.func.isRequired,
  playing: PropTypes.bool.isRequired,
  showChapterNavigationMarkers: PropTypes.bool.isRequired,
  showChapterNumber: PropTypes.bool.isRequired,
  showCoutdownTimerInControls: PropTypes.bool.isRequired,
  showCuepoints: PropTypes.bool.isRequired,
  showCurrentChapterMarker: PropTypes.bool.isRequired,
  showSeekButtons: PropTypes.bool.isRequired,
  showSettingsMenu: PropTypes.oneOf(["hide", "auto", "force"]).isRequired,
  showSliderAboveControls: PropTypes.bool.isRequired,
  stateKey: PropTypes.string.isRequired,
  time: PropTypes.number.isRequired,
  waitingForInitialPlay: PropTypes.bool.isRequired,
};

export default ChapterControls;
